/**
 * Blocks: what a model step can be given. Each renders its own part of the
 * prompt and says when it may be left out to fit the token budget.
 *
 * To give a model something new, add a block here: what it needs from the
 * state (for spec checking), the text it adds to the system prompt or the
 * user turn, and a `drop` rank if it may be left out. Then name it in a spec's
 * `in` list.
 */

import type { CategoryOption } from '../../categories.js';
import { buildCategoryInstructions, buildOcrContext, buildOcrOnlyContext } from '../../prompt.js';
import type { ProviderImage } from '../../providers/core.js';
import type { CompanyInfo, Key, OcrLine } from '../types.js';
import type { AskName } from './asks.js';

export type BlockName = 'image' | 'ocr-text' | 'lines' | 'unsure' | 'company' | 'categories';

/** What a block renders from. */
export interface BlockContext {
  asks: readonly AskName[];
  image: ProviderImage | null;
  /** Whether the step sends the image: without it, the OCR text is all the model has. */
  seesImage: boolean;
  text: string | null;
  /** The OCR lines' text, numbered from 1 in the prompt. */
  lines: readonly string[];
  /** The same lines with the OCR engine's detail, where the client kept it. */
  ocrLines: readonly OcrLine[];
  company: CompanyInfo | null;
  categories: readonly CategoryOption[];
  /** Whether a filing ask is in the step. */
  files: boolean;
}

export interface Block {
  name: BlockName;
  /** Keys the state must offer for the block to have anything to say. */
  needs: Key[];
  /**
   * May be left out to fit the budget; the lowest rank goes first. Without a
   * rank a block is never dropped.
   */
  drop?: number;
  /** Sends the picture. */
  image?: boolean;
  /** Text appended to the system prompt, after a blank line; `null` for none. */
  system?(context: BlockContext): string | null;
  /** Text appended to the user turn, after a blank line; `null` for none. */
  user?(context: BlockContext): string | null;
}

/** The photo. */
const image: Block = { name: 'image', needs: ['image'], image: true };

/** The OCR text as a hint the image overrules, or as the receipt itself when no image is sent. */
const ocrContext = (context: BlockContext, text: string) => (context.seesImage ? buildOcrContext(text) : buildOcrOnlyContext(text));

/** The OCR text as a hint the image overrules (production's `buildOcrContext`). */
const ocrText: Block = {
  name: 'ocr-text',
  needs: ['text'],
  drop: 2,
  user: (context) => (context.text?.trim() ? ocrContext(context, context.text) : null),
};

/** The OCR lines, numbered, as the same hint: what item references point at. */
const lines: Block = {
  name: 'lines',
  needs: ['lines'],
  drop: 2,
  user: (context) =>
    context.lines.length > 0 ? ocrContext(context, context.lines.map((line, index) => `${index + 1}: ${line}`).join('\n')) : null,
};

/**
 * The readings the OCR engine was unsure of, with the alternatives it
 * weighed, by line number: where the model should look twice.
 */
const unsure: Block = {
  name: 'unsure',
  needs: ['lines'],
  drop: 4,
  user: (context) => {
    const rows = context.ocrLines.flatMap((line, index) =>
      (line.fragments ?? [])
        .filter((fragment) => fragment.confidence < 1 && fragment.candidates.length > 0)
        .map((fragment) => `${index + 1}: "${fragment.text}" (or ${fragment.candidates.slice(0, 2).map((reading) => `"${reading}"`).join(', ')})`),
    );
    return rows.length > 0
      ? `The OCR was unsure of these readings; check them against the image. By line, with the other readings it considered:\n${rows.join('\n')}`
      : null;
  },
};

/** What the company register says about the organisation number on the receipt. */
export function companyText(company: CompanyInfo): string {
  const industry = company.industry ? `, industry: ${company.industry}` : '';
  return `The company register lists the organisation number on this receipt as ${company.name}${industry}.`;
}

const company: Block = {
  name: 'company',
  needs: [],
  drop: 3,
  user: (context) => (context.company ? companyText(context.company) : null),
};

/**
 * The category list and the rules for filing, when the step files
 * (production's `buildCategoryInstructions`). Needs nothing: with no list
 * there is nothing to file under, and the step reads without it, as
 * production does.
 */
const categories: Block = {
  name: 'categories',
  needs: [],
  drop: 1,
  system: (context) => (context.files && context.categories.length > 0 ? buildCategoryInstructions(context.categories) : null),
};

export const BLOCKS: Record<BlockName, Block> = {
  image,
  'ocr-text': ocrText,
  lines,
  unsure,
  company,
  categories,
};
